import { useState, type FormEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, Badge, PageHeader } from '@/components/ui/primitives';
import { EngineDashboard } from '@/components/EngineDashboard';
import { ErrorState, LoadingState } from '@/components/DataState';
import { fetchSeoSpend, updateSeoBudget } from '@/data/sources';
import { ApiError } from '@/lib/api';
import { useAuth } from '@/auth/AuthContext';

/* /seo/spend — workspace provider spend vs the monthly budget cap
 * (GET /v1/seo/spend). Big honest numbers only: the figures are the real
 * cost-ledger telemetry, rendered to the penny. £0.00 spent is real data, not
 * an error. The cap is a true hard stop — every paid provider call is
 * pre-priced and charged against the budget BEFORE it is made; a call that
 * would exceed it is refused with nothing spent. Owners/operators can change
 * the cap inline (PATCH /v1/seo/budget, £0–£10,000 mirroring the contract's
 * 0–1,000,000 pence bounds); other roles see it read-only. */

/** Contract bound: budget_pence max 1,000,000 (= £10,000); min 0. */
const MAX_BUDGET_PENCE = 1_000_000;

/** Pence-accurate money — spend telemetry deserves the exact figure
 *  (formatCurrency in lib/utils rounds to whole pounds for dashboards). */
function formatPounds(value: number, currency = 'GBP'): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

/** "2026-07" → "July 2026" (falls back to the raw value if unparseable). */
function monthLabel(month: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(month);
  if (!m) return month;
  const date = new Date(Number(m[1]), Number(m[2]) - 1, 1);
  return new Intl.DateTimeFormat('en-GB', { month: 'long', year: 'numeric' }).format(date);
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-text-tertiary">{label}</div>
      <div className="mt-0.5 font-mono text-sm font-semibold text-text-primary" data-tabular>
        {value}
      </div>
    </div>
  );
}

export function SeoSpendPage() {
  const { activeWorkspace } = useAuth();
  const canManage = activeWorkspace?.role === 'owner' || activeWorkspace?.role === 'operator';
  const queryClient = useQueryClient();
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['seo', 'spend'],
    queryFn: fetchSeoSpend,
  });

  const [editing, setEditing] = useState(false);
  const [budgetInput, setBudgetInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const handleSaveBudget = async (e: FormEvent) => {
    e.preventDefault();
    const raw = budgetInput.trim().replace(/^£/, '');
    const pounds = Number(raw);
    if (raw === '' || !Number.isFinite(pounds)) {
      setSaveError('Enter the new cap in pounds — e.g. 25 or 32.50.');
      return;
    }
    const pence = Math.round(pounds * 100);
    if (Math.abs(pounds * 100 - pence) > 1e-6) {
      setSaveError('The cap is stored to the penny — use at most two decimal places.');
      return;
    }
    if (pence < 0 || pence > MAX_BUDGET_PENCE) {
      setSaveError('The cap must be between £0.00 and £10,000.00.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      // PATCH /v1/seo/budget returns the same Spend snapshot shape as GET
      // /v1/seo/spend — adopt it immediately, then invalidate to reconfirm.
      const snapshot = await updateSeoBudget(pence);
      queryClient.setQueryData(['seo', 'spend'], snapshot);
      await queryClient.invalidateQueries({ queryKey: ['seo', 'spend'] });
      // The Projects overview also surfaces the cap, so refresh it too — a
      // budget change made here must not leave that screen showing a stale cap.
      await queryClient.invalidateQueries({ queryKey: ['seo', 'overview'] });
      setEditing(false);
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) {
        setSaveError(err.message || 'The API rejected that value — check it and try again.');
      } else if (err instanceof ApiError && err.status === 403) {
        setSaveError('Your role can’t change the budget cap — that needs a workspace owner or operator.');
      } else {
        setSaveError(
          err instanceof ApiError && err.message
            ? `${err.message} The cap was not changed — try again.`
            : 'The cap could not be updated. Nothing was changed — try again.',
        );
      }
    } finally {
      setSaving(false);
    }
  };

  if (isPending) {
    return (
      <div className="space-y-6">
        <PageHeader title="Spend & Budget" />
        <LoadingState rows={3} />
      </div>
    );
  }
  if (error) {
    return (
      <div className="space-y-6">
        <PageHeader title="Spend & Budget" />
        <ErrorState error={error} onRetry={() => void refetch()} />
      </div>
    );
  }

  const { month, spent, budget, currency } = data;
  const pct = budget > 0 ? Math.min(100, (spent / budget) * 100) : 0;
  const nearCap = budget > 0 && spent / budget > 0.85;
  const atCap = budget > 0 && spent >= budget;
  const remaining = Math.max(0, budget - spent);

  return (
    <div className="space-y-6">
      <PageHeader title="Spend & Budget" />

      <EngineDashboard
        engineName="SEO Engine"
        heroLabel={`Provider spend — ${monthLabel(month)}`}
        hero={<span data-tabular>{formatPounds(spent, currency)}</span>}
        heroSub={`of a ${formatPounds(budget, currency)} monthly cap. Real cost-ledger telemetry for paid provider calls (DataForSEO) — every call is pre-priced and charged against this budget before it is made.`}
      >
        {atCap ? (
          <div
            role="status"
            className="rounded-md border border-warning/20 bg-warning/10 px-3 py-2 text-sm text-warning"
          >
            Budget cap reached — further paid provider calls are refused before anything is spent.
            Checks resume when the budget resets next month.
          </div>
        ) : nearCap ? (
          <div
            role="status"
            className="rounded-md border border-warning/20 bg-warning/10 px-3 py-2 text-sm text-warning"
          >
            {pct.toFixed(0)}% of this month’s budget is used — provider calls hard-stop at the cap,
            so nothing can overspend.
          </div>
        ) : null}

        <Card className="p-4">
          <div className="mb-1 flex items-center justify-between text-[11px] text-text-tertiary">
            <span>Monthly provider spend</span>
            <span className="font-mono" data-tabular>
              {formatPounds(spent, currency)} / {formatPounds(budget, currency)} cap
            </span>
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-inset">
            <div
              className={nearCap ? 'h-full bg-warning' : 'h-full bg-engine'}
              style={{ width: `${pct}%` }}
            />
          </div>
          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Stat label="Spent" value={formatPounds(spent, currency)} />
            <Stat label="Budget cap" value={formatPounds(budget, currency)} />
            <Stat label="Remaining" value={formatPounds(remaining, currency)} />
          </div>
          {spent === 0 && (
            <p className="mt-3 text-sm text-text-secondary">
              No paid provider calls have been made this month — {formatPounds(0, currency)} of
              your {formatPounds(budget, currency)} cap is used. That’s the real ledger figure,
              not missing data.
            </p>
          )}
        </Card>

        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div className="text-sm font-medium text-text-primary">How the cap works</div>
            <Badge tone="compliance">hard stop</Badge>
          </div>
          <p className="mt-2 text-sm text-text-secondary">
            Every paid provider call (rank checks, competitor snapshots, on-page audits) is priced
            up front and charged against this budget before the provider is contacted. A call that
            would exceed the cap is refused — no provider request is made and nothing is spent.
          </p>
          {canManage ? (
            editing ? (
              <form onSubmit={(e) => void handleSaveBudget(e)} className="mt-3 space-y-2">
                <label className="block">
                  <span className="mb-1 block text-xs font-medium text-text-secondary">
                    New monthly cap (£)
                  </span>
                  <div className="flex items-center gap-2">
                    <input
                      inputMode="decimal"
                      autoFocus
                      value={budgetInput}
                      disabled={saving}
                      onChange={(e) => setBudgetInput(e.target.value)}
                      placeholder="25.00"
                      aria-label="New monthly budget cap in pounds"
                      className="h-control w-32 rounded-md border border-border-default bg-canvas px-3 text-sm text-text-primary outline-none focus:border-ai focus:ring-2 focus:ring-ai/30 disabled:opacity-60"
                    />
                    <Button type="submit" variant="primary" disabled={saving}>
                      {saving ? 'Saving…' : 'Save cap'}
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      disabled={saving}
                      onClick={() => {
                        setEditing(false);
                        setSaveError(null);
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                  <span className="mt-1 block text-[11px] text-text-tertiary">
                    £0.00–£10,000.00, to the penny. Takes effect immediately for every paid
                    provider call in this workspace.
                  </span>
                </label>
                {saveError && (
                  <p role="alert" className="text-sm text-danger">
                    {saveError}
                  </p>
                )}
              </form>
            ) : (
              <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                <p className="text-xs text-text-tertiary">
                  The cap is a workspace-wide setting — changing it applies to every paid provider
                  call immediately.
                </p>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setBudgetInput(budget.toFixed(2));
                    setSaveError(null);
                    setEditing(true);
                  }}
                >
                  Change budget cap
                </Button>
              </div>
            )
          ) : (
            <p className="mt-3 text-xs text-text-tertiary">
              The monthly cap is a workspace setting — changing it needs a workspace owner or
              operator.
            </p>
          )}
        </Card>
      </EngineDashboard>
    </div>
  );
}
